import { useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useEffectiveExperience } from '@/features/experiences/contexts/PageExperienceContext';
import { gqlClient } from '@/lib';
import { createExperienceCollaboratorsQueryKey } from '@/utils/queryKeys';
import type {
  GetExperienceCollaboratorsQuery,
  GetExperienceCollaboratorsQueryVariables,
} from './useCollaborators.generated';

const query = gql`
  query GetExperienceCollaborators($input: GetExperiencePermissionsInput!) {
    getExperiencePermissions(input: $input) {
      all {
        id
        email
        name
        avatar
        role
      }
      organizations {
        organizationId
        name
        role
      }
    }
  }
`;

const selector = (data: GetExperienceCollaboratorsQuery) => ({
  collaborators: data.getExperiencePermissions.all ?? [],
  organizationPermissions: data.getExperiencePermissions.organizations ?? [],
});

export const useCollaborators = () => {
  // Use the effective experience (the parent planner when the context experience is a
  // printable child) so the viewer reads permissions they're actually allowed to —
  // editors get a 403 reading a child's permissions but can read the parent planner's.
  const effectiveExperience = useEffectiveExperience();
  const experienceId = String(effectiveExperience.id);

  const { data, isLoading } = useQuery({
    queryKey: createExperienceCollaboratorsQueryKey(experienceId),
    enabled: Boolean(effectiveExperience),
    select: selector,
    staleTime: 10_000,
    queryFn: () => {
      return gqlClient.request<
        GetExperienceCollaboratorsQuery,
        GetExperienceCollaboratorsQueryVariables
      >(query, { input: { experienceId } });
    },
  });

  return {
    collaborators: data?.collaborators ?? [],
    organizationPermissions: data?.organizationPermissions ?? [],
    isLoading,
  };
};
